import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { feedbackHref } from "../lib/feedbackLink";
import ResponsesSummary from "./ResponsesSummary";
import { TrendPill } from "./common";
import { Panel, Distribution, SsiTile, Touchpoints, Themes, Recovery, Mini, fmtV } from "./scorecard";
import PeriodFilter from "./PeriodFilter";
import { fmtDate } from "../lib/time";

const delta = (a, b) => (a != null && b != null ? Math.round((a - b) * 100) / 100 : null);

export function EmployeeScorecard({ employeeId }) {
  const [days, setDays] = useState("7");
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const go = (extra) => navigate(feedbackHref({ employee_id: employeeId, days, ...extra }));
  useEffect(() => {
    setD(null);
    api.get(`/scorecards/employee/${employeeId}?days=${days}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [employeeId, days]);

  const picker = <PeriodFilter value={days} onChange={setDays} testId="emp-scorecard-period" />;
  if (!d) return <p className="py-10 text-center text-sm text-slate-400">Loading scorecard…</p>;
  if (d.error) return <p className="py-10 text-center text-sm text-slate-400">Could not load the scorecard.</p>;
  const dSsi = delta(d.ssi, d.previous.ssi);
  const vsOutlet = delta(d.ssi, d.outlet_benchmark.ssi);

  return (
    <div className="space-y-5" data-testid="employee-scorecard">
      <div className="flex justify-end">{picker}</div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SsiTile d={d} testId="esc-ssi" />
        <Mini label="Verified ratings" value={d.ratings} sub={d.completion_rate != null ? `${d.completion_rate}% of ${d.scans} scans completed` : "No scans logged"} />
        <Mini label="Positive recognition (4–5★)" value={fmtV(d.positive_rate, "%")} />
        <div className="rounded-lg bg-slate-50 p-3" data-testid="esc-trend">
          <p className="text-xs text-slate-400">vs {d.days === 0 ? "yesterday" : `previous ${d.days} days`} / outlet</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {dSsi != null ? <TrendPill value={dSsi} suffix="" testId="esc-vs-prev" /> : <span className="text-sm text-slate-400">—</span>}
            {vsOutlet != null && <span className="text-xs text-slate-500">outlet {d.outlet_benchmark.ssi} <TrendPill value={vsOutlet} suffix="" testId="esc-vs-outlet" /></span>}
          </div>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Rating distribution" testId="esc-distribution"><Distribution dist={d.distribution} testId="esc-dist-bar" onPick={(s) => go({ rating: s })} /></Panel>
        <Panel title="Touchpoint scores (1–5)"><Touchpoints tp={d.touchpoints} testId="esc-touchpoints" onPick={(k) => go({ dimension: k })} /></Panel>
        <Panel title="Service recovery"><Recovery r={d.recovery} testId="esc-recovery" /></Panel>
        <Panel title="Comment themes & commendations" testId="esc-themes">
          <Themes items={d.themes} testId="esc-theme-list" onPick={(t) => go({ theme: t })} />
          {d.commendations.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
              {d.commendations.map((c, i) => <li key={i} className="text-slate-600">“{c.note}” <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span></li>)}
            </ul>
          )}
        </Panel>
      </div>
      <ResponsesSummary employeeId={employeeId} days={days} testId="emp-responses-summary" />
    </div>
  );
}
